import { useState, useEffect, useCallback } from 'react';
import { LaborCostCustomItemMst, getList, save, deleteCustomItem } from 'api/laborCostCustomItemMst';
import useToastNotification from 'hooks/useToastNotification';

// 登録上限はサーバー側（company.labor_cost_custom_item_limit）で制御

export const useLaborCostCustomItemList = () => {
  const [itemList, setItemList] = useState<Array<LaborCostCustomItemMst>>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchList = useCallback(() => {
    const companyCode = sessionStorage.getItem('loginUser.companyCode') || '';
    setIsLoading(true);
    getList(companyCode)
      .then((response) => {
        setItemList(response);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { itemList, setItemList, fetchList, isLoading };
};

export const useLaborCostCustomItemForm = (fetchList: () => void) => {
  const { successNotification, errorNotification } = useToastNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [editItem, setEditItem] = useState<LaborCostCustomItemMst>({
    customId: null,
    companyCode: '',
    customItemName: '',
    dispOrder: 0,
    includeAttend: true,
    includeSales: true,
  });

  const openCreateForm = useCallback(() => {
    setEditItem({
      customId: null,
      companyCode: '',
      customItemName: '',
      dispOrder: 0,
      includeAttend: true,
      includeSales: true,
    });
    setIsEditing(true);
  }, []);

  const openEditForm = useCallback((item: LaborCostCustomItemMst) => {
    setEditItem({ ...item });
    setIsEditing(true);
  }, []);

  const closeForm = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(() => {
    const companyCode = sessionStorage.getItem('loginUser.companyCode') || '';
    const loginStaffCode = sessionStorage.getItem('loginUser.staffCode') || '';

    if (!editItem.customItemName.trim()) {
      errorNotification('項目名を入力してください。');
      return;
    }

    save(companyCode, loginStaffCode, editItem)
      .then(() => {
        successNotification(editItem.customId ? '更新しました。' : '登録しました。');
        setIsEditing(false);
        fetchList();
      })
      .catch((error) => {
        if (error.response && error.response.data && error.response.data.defaultMessage) {
          errorNotification(error.response.data.defaultMessage);
        } else {
          errorNotification('サーバー側でエラーが発生しました。');
        }
      });
  }, [editItem, fetchList, successNotification, errorNotification]);

  const handleDelete = useCallback((customId: string) => {
    const companyCode = sessionStorage.getItem('loginUser.companyCode') || '';
    deleteCustomItem(companyCode, customId)
      .then(() => {
        successNotification('削除しました。');
        fetchList();
      })
      .catch((error) => {
        if (error.response && error.response.data && error.response.data.defaultMessage) {
          errorNotification(error.response.data.defaultMessage);
        } else {
          errorNotification('サーバー側でエラーが発生しました。');
        }
      });
  }, [fetchList, successNotification, errorNotification]);

  return {
    isEditing,
    editItem,
    setEditItem,
    openCreateForm,
    openEditForm,
    closeForm,
    handleSave,
    handleDelete,
  };
};
